import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import supabase from '../../supabase';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { AuthShell, AuthHeading } from '../../components/auth/auth-shell';

type VerifyState = 'loading' | 'error';

type ErrorView = {
  title: string;
  description: string;
  primaryAction: { label: string; to: string };
  secondaryAction?: { label: string; to: string };
};

const PRIMARY_BUTTON_CLASS =
  'inline-flex items-center justify-center h-12 w-full bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full px-6 text-sm font-medium transition-colors';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<VerifyState>('loading');
  const [errorView, setErrorView] = useState<ErrorView | null>(null);

  useEffect(() => {
    const params = readFragmentParams();

    if (hashHasError(params)) {
      setErrorView(viewFromErrorParams(params));
      setState('error');
      return;
    }

    if (!params.get('access_token')) {
      setErrorView(missingTokenView());
      setState('error');
      return;
    }

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        navigate('/organization', { replace: true });
        return;
      }
      if (event !== 'INITIAL_SESSION') return;
      if (session) {
        navigate('/organization', { replace: true });
        return;
      }

      setErrorView(genericErrorView());
      setState('error');
    });

    return () => data.subscription.unsubscribe();
  }, [navigate]);

  if (state === 'loading') return <CenteredSpinner />;
  return <ErrorCard view={errorView!} />;
}

function hashHasError(params: URLSearchParams) {
  return Boolean(params.get('error_code') || params.get('error_description'));
}

function viewFromErrorParams(params: URLSearchParams): ErrorView {
  const code = params.get('error_code');
  if (code === 'otp_expired' || code === 'access_denied') {
    return expiredLinkView();
  }
  return genericErrorView();
}

function readFragmentParams() {
  return new URLSearchParams(window.location.hash.substring(1));
}

function expiredLinkView(): ErrorView {
  return {
    title: 'This link has expired',
    description:
      'Verification links are only valid for a short time. Send yourself a new one.',
    primaryAction: { label: 'Send a new link', to: '/resend-verification' },
    secondaryAction: { label: 'Back to sign in', to: '/sign-in' },
  };
}

function missingTokenView(): ErrorView {
  return {
    title: 'Nothing to verify here',
    description:
      'It looks like you opened this page directly. Please use the link in your verification email.',
    primaryAction: { label: 'Back to sign in', to: '/sign-in' },
  };
}

function genericErrorView(): ErrorView {
  return {
    title: 'Something went wrong',
    description:
      "We couldn't verify your email. Try sending a new link, or sign in if you've already verified.",
    primaryAction: { label: 'Send a new link', to: '/resend-verification' },
    secondaryAction: { label: 'Back to sign in', to: '/sign-in' },
  };
}

function CenteredSpinner() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-bg-main">
      <LoadingSpinner />
    </div>
  );
}

function ErrorCard({ view }: { view: ErrorView }) {
  return (
    <AuthShell>
      <AuthHeading title={view.title} description={view.description} />
      <Link to={view.primaryAction.to} className={PRIMARY_BUTTON_CLASS}>
        {view.primaryAction.label}
      </Link>
      {view.secondaryAction && (
        <div className="text-center">
          <Link
            to={view.secondaryAction.to}
            className="text-sm text-accent-purple hover:text-accent-purple/80"
          >
            {view.secondaryAction.label}
          </Link>
        </div>
      )}
    </AuthShell>
  );
}
